import { useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import {
  getApiMessage,
  getHitLabAttribution,
  isHitLabClientEnabled,
} from './hitLabUtils';
import './hitLab.css';

export default function HitLabIntake() {
  const { player } = useContext(AuthContext);
  const location = useLocation();
  const attribution = useMemo(
    () => getHitLabAttribution(location.search),
    [location.search],
  );
  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState(null);
  const [payment, setPayment] = useState({
    amountCents: 999,
    currency: 'usd',
    priceDisplay: '$9.99',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const clientEnabled = isHitLabClientEnabled();
  const query = useMemo(
    () => new URLSearchParams(location.search || ''),
    [location.search],
  );

  useEffect(() => {
    if (!clientEnabled) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    axiosConn
      .get('hitlab/intake/me')
      .then((response) => {
        if (!active) return;
        setIntake(response.data?.data?.intake || null);
        if (response.data?.data?.payment) {
          setPayment(response.data.data.payment);
        }
      })
      .catch((err) => {
        if (active) {
          setError(
            getApiMessage(
              err,
              'Artist intake is temporarily unavailable. Please try again later.',
            ),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientEnabled]);

  const startCheckout = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await axiosConn.post('hitlab/intake/checkout', {
        attribution,
      });
      const checkoutUrl = response.data?.data?.checkoutUrl;
      if (!checkoutUrl) throw new Error('missing-checkout-url');
      window.location.assign(checkoutUrl);
    } catch (err) {
      if (err?.response?.status === 409) {
        setError('Your artist intake payment is already confirmed. Continue to song submission.');
      } else {
        setError(
          getApiMessage(
            err,
            'Checkout could not be started. Please try again.',
          ),
        );
      }
      setBusy(false);
    }
  };

  const paid =
    intake?.paymentStatus === 'paid' || intake?.status === 'paid_pending_upload';

  return (
    <main className="hitlab-page hitlab-workflow">
      <Helmet>
        <title>JG HitLab Intake | Payment</title>
      </Helmet>
      <section className="hitlab-shell hitlab-workflow-grid">
        <div className="hitlab-workflow-copy">
          <p className="hitlab-kicker">ARTIST INTAKE</p>
          <h1>Continue to Intake - {payment.priceDisplay || '$9.99'}</h1>
          <p className="hitlab-lede">
            Payment gets your song processed. Excellence gets your song played.
          </p>
          <p>
            Your payment covers processing, file handling, rights screening,
            review queue entry, and curator listening. It does not buy approval,
            placement, rotation, positive feedback, Hit Radar, public promotion,
            or any outcome.
          </p>
          {query.get('checkout') === 'cancel' ? (
            <div className="hitlab-alert">Checkout was canceled. You can restart when ready.</div>
          ) : null}
          {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
          {!clientEnabled ? (
            <div className="hitlab-alert">Artist intake is not accepting new submissions right now.</div>
          ) : loading ? (
            <div className="hitlab-alert">Loading intake status...</div>
          ) : paid ? (
            <Link className="hitlab-primary-link" to="/hitlab/submit">
              Continue to song submission
            </Link>
          ) : (
            <button
              type="button"
              className="hitlab-primary-btn"
              onClick={startCheckout}
              disabled={busy || !player?.token}
            >
              {busy ? 'Opening secure checkout...' : `Continue to Intake - ${payment.priceDisplay || '$9.99'}`}
            </button>
          )}
        </div>
        <aside className="hitlab-checkout-panel">
          <span>WHAT THIS IS</span>
          <h2>Processing, not placement.</h2>
          <ul>
            <li>One original song</li>
            <li>Private HTTPS song link after payment</li>
            <li>Rights confirmations required</li>
            <li>Curator listening after processing</li>
          </ul>
        </aside>
      </section>
    </main>
  );
}
